/**
 * Workflow Output Store
 *
 * Hybrid storage for workflow outputs that enables reliable context passing
 * between agentic workflows via button clicks.
 *
 * Features:
 * - In-memory cache for fast access
 * - localStorage persistence for page refresh survival
 * - Automatic cleanup to prevent unbounded growth
 * - Data attribute support for explicit context passing
 * - Document analysis storage for unified document reader
 */

import type { TranslatableContent } from '../agenticWorkflows/types';
import type { TokenUsageMetadata } from '../../components/GlobalChatSystem/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Stored workflow output with unique ID for retrieval.
 */
export interface StoredWorkflowOutput {
  /** Unique identifier for this output */
  id: string;
  /** The actual content from the workflow */
  content: string;
  /** Title/label for the content */
  title: string;
  /** When the output was created */
  timestamp: Date;
  /** Source workflow type */
  source: 'research' | 'formatted' | 'video_script' | 'translation' | 'document_analysis' | 'document_analysis_failed';
  /** Additional metadata (e.g., original query, document analysis) */
  metadata?: Record<string, unknown>;
  /** Token usages accumulated from all steps that produced this output */
  tokenUsages?: TokenUsageMetadata[];
}

/**
 * Context passed from button click to workflow.
 */
export interface ButtonClickContext {
  /** Workflow output associated with the clicked button */
  workflowOutput?: StoredWorkflowOutput | null;
  /** The button element that was clicked */
  buttonElement?: HTMLButtonElement;
  /** Additional data attributes from the button (values can be undefined per HTMLElement.dataset) */
  dataAttributes?: Record<string, string | undefined>;
  /** Document analysis linked to this workflow output */
  linkedDocumentId?: string;
  /**
   * Finding 5 (2026-07-15): the ACTIVE thread id at button-click time
   * (buttons render inside a thread's transcript, so the active thread IS
   * the button's home thread). Button paths pass this explicitly to
   * getOrCreateRuntime so button-created runtimes bind to the clicking
   * thread — an unbound or stale-bound runtime makes canRuntimeCaptureSend
   * reject the user's same-thread typed reply to an awaiting-input tool.
   * `null` = single-thread surface (no thread switcher).
   */
  threadId?: string | null;
}

/**
 * Stored document analysis for unified document reader.
 * Separate from workflow outputs to track document context across workflows.
 */
export interface StoredDocumentAnalysis {
  /** Unique identifier for this analysis */
  id: string;
  /** The analysis text content */
  analysis: string;
  /** Number of files that were analyzed */
  filesAnalyzed: number;
  /** Names of files that were analyzed */
  fileNames: string[];
  /** When the analysis was performed */
  timestamp: Date;
  /** Reference to workflow output that used this analysis */
  linkedWorkflowOutputId?: string;
  /** Source that initiated the analysis */
  source: 'pre-orchestrator' | 'orchestrator' | 'direct-tool' | 'button-click' | 'workflow';
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'prest0_workflow_outputs';
const MAX_STORED = 10; // Keep last 10 outputs to prevent unbounded growth

// ============================================================================
// In-Memory Store
// ============================================================================

/**
 * In-memory cache for fast access.
 * This survives React re-renders but not page refreshes.
 */
const memoryStore = new Map<string, StoredWorkflowOutput>();

/**
 * In-memory cache for document analyses.
 * Separate from workflow outputs to track document context.
 */
const documentAnalysisStore = new Map<string, StoredDocumentAnalysis>();

// ============================================================================
// LocalStorage Sync
// ============================================================================

/**
 * Serialize a StoredWorkflowOutput for localStorage.
 */
function serializeOutput(output: StoredWorkflowOutput): Record<string, unknown> {
  return {
    ...output,
    timestamp: output.timestamp instanceof Date
      ? output.timestamp.toISOString()
      : output.timestamp,
  };
}

/**
 * Deserialize a StoredWorkflowOutput from localStorage.
 */
function deserializeOutput(data: Record<string, unknown>): StoredWorkflowOutput {
  return {
    ...data,
    timestamp: new Date(data.timestamp as string),
  } as StoredWorkflowOutput;
}

/**
 * Sync in-memory store to localStorage.
 * Only stores the most recent MAX_STORED outputs.
 */
function syncToStorage(): void {
  const outputs = Array.from(memoryStore.values())
    .sort((a, b) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : 0;
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : 0;
      return timeA - timeB;
    })
    .slice(-MAX_STORED)
    .map(serializeOutput);

  // Quota is a REAL ceiling here, not a theoretical one (2026-08-20): a
  // 200-page translation banks ~500K chars, and MAX_STORED is 10 with no
  // per-output size cap. setItem is all-or-nothing, so one oversized history
  // used to make EVERY output fail to persist — silently, because the catch
  // only warned. That is how a twenty-minute translation disappears on a
  // refresh.
  //
  // The newest output is the one someone is about to lose, so on failure drop
  // the oldest and retry, down to keeping just that one. Persisting less is a
  // far better outcome than persisting nothing.
  for (let keep = outputs.length; keep >= 1; keep--) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(outputs.slice(-keep)));
      if (keep < outputs.length) {
        console.warn(`[WorkflowOutputStore] Storage quota — persisted the newest ${keep} of ${outputs.length} outputs`);
      }
      return;
    } catch {
      // Try again with fewer entries.
    }
  }
  console.warn('[WorkflowOutputStore] Failed to sync to localStorage: even the newest output exceeds quota');
}

/**
 * Hydrate in-memory store from localStorage.
 * Called on module load.
 */
function hydrateFromStorage(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const outputs = JSON.parse(stored) as Record<string, unknown>[];
      outputs.forEach((data) => {
        const output = deserializeOutput(data);
        memoryStore.set(output.id, output);
      });
      console.log(`[WorkflowOutputStore] Hydrated ${outputs.length} outputs from localStorage`);
    }
  } catch (e) {
    console.warn('[WorkflowOutputStore] Failed to hydrate from localStorage:', e);
  }
}

// Initialize on module load
hydrateFromStorage();

// ============================================================================
// Public API
// ============================================================================

/**
 * Generate a unique ID for a workflow output.
 */
export function generateOutputId(prefix: string = 'wo'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Store a workflow output and return its ID.
 * If output already has an ID, uses that; otherwise generates one.
 */
export function storeWorkflowOutput(
  output: Omit<StoredWorkflowOutput, 'id'> & { id?: string }
): string {
  const id = output.id || generateOutputId();
  const stored: StoredWorkflowOutput = {
    ...output,
    id,
    timestamp: output.timestamp instanceof Date ? output.timestamp : new Date(output.timestamp),
  };

  memoryStore.set(id, stored);
  syncToStorage();

  console.log(`[WorkflowOutputStore] Stored output: ${id} (${stored.source})`);
  return id;
}

/**
 * Retrieve a workflow output by ID.
 * Returns null if not found.
 */
export function getWorkflowOutput(id: string): StoredWorkflowOutput | null {
  const output = memoryStore.get(id);
  if (output) {
    console.log(`[WorkflowOutputStore] Retrieved output: ${id}`);
  }
  return output || null;
}

/**
 * Check if a workflow output exists.
 */
export function hasWorkflowOutput(id: string): boolean {
  return memoryStore.has(id);
}

/**
 * Get the most recent output by source type.
 */
export function getLatestOutputBySource(
  source: StoredWorkflowOutput['source']
): StoredWorkflowOutput | null {
  let latest: StoredWorkflowOutput | null = null;
  let latestTime = 0;

  for (const output of memoryStore.values()) {
    if (output.source === source) {
      const time = output.timestamp instanceof Date
        ? output.timestamp.getTime()
        : new Date(output.timestamp).getTime();
      if (time > latestTime) {
        latest = output;
        latestTime = time;
      }
    }
  }

  return latest;
}

/**
 * Get the most recent workflow output regardless of source type.
 * Useful as a final fallback during workflow chaining when the caller
 * only needs the freshest available transformed content.
 */
export function getLatestOutput(): StoredWorkflowOutput | null {
  let latest: StoredWorkflowOutput | null = null;
  let latestTime = 0;

  for (const output of memoryStore.values()) {
    const time = output.timestamp instanceof Date
      ? output.timestamp.getTime()
      : new Date(output.timestamp).getTime();

    if (time > latestTime) {
      latest = output;
      latestTime = time;
    }
  }

  return latest;
}

/**
 * Get all stored outputs.
 */
export function getAllOutputs(): StoredWorkflowOutput[] {
  return Array.from(memoryStore.values());
}

/**
 * Clear old outputs, keeping only the most recent MAX_STORED.
 * Call this when starting a new research workflow.
 */
export function clearOldOutputs(): void {
  const outputs = Array.from(memoryStore.entries())
    .sort(([, a], [, b]) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : 0;
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : 0;
      return timeA - timeB;
    })
    .slice(-MAX_STORED);

  memoryStore.clear();
  outputs.forEach(([k, v]) => memoryStore.set(k, v));
  syncToStorage();

  console.log(`[WorkflowOutputStore] Cleared old outputs, keeping ${outputs.length}`);
}

/**
 * Clear all stored outputs.
 * Use sparingly - mainly for testing.
 */
export function clearAllOutputs(): void {
  memoryStore.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('[WorkflowOutputStore] Failed to clear localStorage:', e);
  }
}

// ============================================================================
// Conversion Helpers
// ============================================================================

/**
 * Convert a StoredWorkflowOutput to TranslatableContent.
 * Useful for bridging to the existing shared context system.
 */
export function toTranslatableContent(output: StoredWorkflowOutput): TranslatableContent {
  return {
    content: output.content,
    title: output.title,
    timestamp: output.timestamp,
    source: output.source === 'translation' ? 'formatted' : output.source,
    tokenUsages: output.tokenUsages,
  };
}

/**
 * Convert TranslatableContent to StoredWorkflowOutput.
 * Generates a new ID.
 */
export function fromTranslatableContent(
  content: TranslatableContent,
  idPrefix: string = 'tc'
): StoredWorkflowOutput {
  return {
    id: generateOutputId(idPrefix),
    content: content.content,
    title: content.title,
    timestamp: content.timestamp,
    source: content.source,
  };
}

// ============================================================================
// Document Analysis Storage
// ============================================================================

const DOCUMENT_STORAGE_KEY = 'prest0_document_analyses';
const MAX_DOCUMENT_STORED = 5; // Keep fewer document analyses

/**
 * Store a document analysis and return its ID.
 */
export function storeDocumentAnalysis(
  analysis: Omit<StoredDocumentAnalysis, 'id'> & { id?: string }
): string {
  const id = analysis.id || generateOutputId('doc');
  const stored: StoredDocumentAnalysis = {
    ...analysis,
    id,
    timestamp: analysis.timestamp instanceof Date ? analysis.timestamp : new Date(analysis.timestamp),
  };

  documentAnalysisStore.set(id, stored);
  syncDocumentAnalysesToStorage();

  console.log(`[WorkflowOutputStore] Stored document analysis: ${id} (${stored.filesAnalyzed} files)`);
  return id;
}

/**
 * Retrieve a document analysis by ID.
 */
export function getDocumentAnalysis(id: string): StoredDocumentAnalysis | null {
  const analysis = documentAnalysisStore.get(id);
  if (analysis) {
    console.log(`[WorkflowOutputStore] Retrieved document analysis: ${id}`);
  }
  return analysis || null;
}

/**
 * Get the most recent document analysis.
 */
export function getLatestDocumentAnalysis(): StoredDocumentAnalysis | null {
  let latest: StoredDocumentAnalysis | null = null;
  let latestTime = 0;

  for (const analysis of documentAnalysisStore.values()) {
    const time = analysis.timestamp instanceof Date
      ? analysis.timestamp.getTime()
      : new Date(analysis.timestamp).getTime();
    if (time > latestTime) {
      latest = analysis;
      latestTime = time;
    }
  }

  return latest;
}

/**
 * Check if a document analysis exists.
 */
export function hasDocumentAnalysis(id: string): boolean {
  return documentAnalysisStore.has(id);
}

/**
 * Clear all document analyses.
 */
export function clearDocumentAnalyses(): void {
  documentAnalysisStore.clear();
  try {
    localStorage.removeItem(DOCUMENT_STORAGE_KEY);
  } catch (e) {
    console.warn('[WorkflowOutputStore] Failed to clear document analyses:', e);
  }
  console.log('[WorkflowOutputStore] Cleared all document analyses');
}

/**
 * Sync document analyses to localStorage.
 */
function syncDocumentAnalysesToStorage(): void {
  const analyses = Array.from(documentAnalysisStore.values())
    .sort((a, b) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : 0;
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : 0;
      return timeA - timeB;
    })
    .slice(-MAX_DOCUMENT_STORED)
    .map((analysis) => ({
      ...analysis,
      timestamp: analysis.timestamp instanceof Date
        ? analysis.timestamp.toISOString()
        : analysis.timestamp,
    }));

  try {
    localStorage.setItem(DOCUMENT_STORAGE_KEY, JSON.stringify(analyses));
  } catch (e) {
    console.warn('[WorkflowOutputStore] Failed to sync document analyses to localStorage:', e);
  }
}

/**
 * Hydrate document analyses from localStorage.
 */
function hydrateDocumentAnalysesFromStorage(): void {
  try {
    const stored = localStorage.getItem(DOCUMENT_STORAGE_KEY);
    if (stored) {
      const analyses = JSON.parse(stored) as Array<Record<string, unknown>>;
      analyses.forEach((data) => {
        const analysis: StoredDocumentAnalysis = {
          ...data,
          timestamp: new Date(data.timestamp as string),
        } as StoredDocumentAnalysis;
        documentAnalysisStore.set(analysis.id, analysis);
      });
      console.log(`[WorkflowOutputStore] Hydrated ${analyses.length} document analyses from localStorage`);
    }
  } catch (e) {
    console.warn('[WorkflowOutputStore] Failed to hydrate document analyses from localStorage:', e);
  }
}

// Initialize document analyses on module load
hydrateDocumentAnalysesFromStorage();

// ============================================================================
// Debug Helpers
// ============================================================================

/**
 * Get store statistics for debugging.
 */
export function getStoreStats(): {
  memoryCount: number;
  outputIds: string[];
  sources: Record<string, number>;
  documentAnalysisCount: number;
} {
  const sources: Record<string, number> = {};
  for (const output of memoryStore.values()) {
    sources[output.source] = (sources[output.source] || 0) + 1;
  }

  return {
    memoryCount: memoryStore.size,
    outputIds: Array.from(memoryStore.keys()),
    sources,
    documentAnalysisCount: documentAnalysisStore.size,
  };
}
